import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { clientPath, useFollowSwitcher } from '../lib/clientLink.js';
import { Field, fmtDateTime } from '../components/FormBits.jsx';
import Working, { pollUntil } from '../components/Working.jsx';
// Session 32: the same components the client review page renders. Leena asked
// to be treated like a real client here, so this page shows her what one sees:
// the callout, one preview at the top, the photo rows named by headline, and
// the edit-the-words door behind a button.
import {
  ReviewCallout, PostAndEditor, ImageSlotsSection, useSlotUploads,
} from '../components/DraftReview.jsx';
import PostingInstructions from '../components/PostingInstructions.jsx';

// Session 16: ad hoc blogs. Agency only, never visible to clients in any mode.
//   /admin/clients/:clientId/adhoc          the client's ad hoc blogs
//   /admin/clients/:clientId/adhoc/new      New ad hoc blog (uploads + Generate)
//   /admin/clients/:clientId/adhoc/:jobId   live status, draft review, Download package
// Uploads go to the private adhoc-uploads bucket (agency-only policies);
// writes go through portal_create_adhoc / portal_edit_adhoc /
// portal_revise_adhoc / portal_approve_adhoc.

export const ADHOC_TYPES = [
  ['case_study', 'Case study'],
  ['trending_seasonal', 'Trending or seasonal'],
  ['brand_story', 'Brand story'],
  ['seo_guide', 'SEO guide'],
];
const TYPE_LABEL = Object.fromEntries(ADHOC_TYPES);
const MAX_FILES = 10;
const MAX_BYTES = 20 * 1024 * 1024;
const MIME = {
  pdf: 'application/pdf', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain', md: 'text/markdown', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
  webp: 'image/webp', gif: 'image/gif', heic: 'image/heic',
};
const extOf = (name) => String(name || '').toLowerCase().split('.').pop();
const isImage = (name) => ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic'].includes(extOf(name));
const safeName = (name) => String(name || 'file').replace(/[^A-Za-z0-9._-]+/g, '_').slice(-80);
const IN_FLIGHT = ['queued', 'running', 'revising', 'approved'];

const STATUS_TEXT = {
  queued: 'Queued. Drafting starts within a couple of minutes.',
  running: 'Drafting from your material. This can take a few minutes.',
  revising: 'Revising with your notes. This can take a few minutes.',
  agency_review: 'Ready for your review.',
  approved: 'Approved. Building the package…',
  done: 'Approved. The package is ready.',
  failed: 'Something went wrong.',
};

export default function AdminAdhoc() {
  const { clientId, jobId } = useParams();
  const { isAgency, activeId, setActiveId, clients } = useAuth();
  useEffect(() => { if (clientId && clientId !== activeId) setActiveId(clientId); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Session 17: a switcher pick opens the picked client's list (or New form); a job page goes to the list.
  useFollowSwitcher(clientId, (id) => `/admin/clients/${id}/adhoc${jobId === 'new' ? '/new' : ''}`);
  if (!isAgency) return <Navigate to="/" replace />;
  const name = clients?.find((c) => c.client_id === clientId)?.name || clientId;
  return (
    <>
      <p><Link to={clientPath(`/admin/clients/${clientId}/settings`, clientId)}>← {name}</Link>
        {jobId && <> · <Link to={clientPath(`/admin/clients/${clientId}/adhoc`, clientId)}>Ad hoc blogs</Link></>}</p>
      {!jobId && <AdhocList clientId={clientId} name={name} />}
      {jobId === 'new' && <AdhocNew clientId={clientId} name={name} />}
      {jobId && jobId !== 'new' && <AdhocJob clientId={clientId} jobId={jobId} />}
    </>
  );
}

function AdhocList({ clientId, name }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('v_portal_adhoc').select('job_id, title, status, adhoc_type, created_at, package_ready')
        .eq('client_id', clientId).order('created_at', { ascending: false });
      if (!cancelled) setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [clientId]);
  return (
    <>
      <h1>Ad hoc blogs: {name}</h1>
      <p className="lede">One-off posts written from your own material. Agency only: {name} never sees these.
        {' '}<Link className="btn btn-primary" to={clientPath(`/admin/clients/${clientId}/adhoc/new`, clientId)}>New ad hoc blog</Link></p>
      {rows === null && <p className="lede">Loading…</p>}
      {rows && rows.length === 0 && <div className="empty"><h2>No ad hoc blogs yet</h2></div>}
      {rows && rows.length > 0 && (
        <div className="card">
          {rows.map((r) => (
            <div className="blog-row" key={r.job_id}>
              <div className="title">
                <Link to={clientPath(`/admin/clients/${clientId}/adhoc/${r.job_id}`, clientId)}>{r.title}</Link>
                <span className="sub"><span className="mode-tag">Ad hoc</span> {TYPE_LABEL[r.adhoc_type] || r.adhoc_type} · {fmtDateTime(r.created_at)}</span>
              </div>
              <div className="date" />
              <div className="actions"><span className="hint" style={{ margin: 0 }}>{STATUS_TEXT[r.status] || r.status}</span></div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function AdhocNew({ clientId, name }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [type, setType] = useState('case_study');
  const [text, setText] = useState('');
  const [notes, setNotes] = useState('');
  const [band, setBand] = useState('utility');
  const [files, setFiles] = useState([]);          // [{file, use_image}]
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);

  function addFiles(list) {
    setErr(null);
    const next = [...files];
    for (const f of Array.from(list || [])) {
      if (!MIME[extOf(f.name)]) { setErr(`${f.name}: use pdf, docx, txt, md or an image.`); continue; }
      if (f.size > MAX_BYTES) { setErr(`${f.name} is over 20 MB.`); continue; }
      if (next.length >= MAX_FILES) { setErr(`Up to ${MAX_FILES} files.`); break; }
      next.push({ file: f, use_image: false });
    }
    setFiles(next);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function generate(e) {
    e.preventDefault();
    if (!title.trim()) { setErr('Add a working title or topic.'); return; }
    setErr(null);
    const uploadId = (crypto.randomUUID?.() || String(Date.now()));
    const uploaded = [];
    for (let i = 0; i < files.length; i += 1) {
      const { file, use_image } = files[i];
      setBusy(`Uploading ${i + 1} of ${files.length}…`);
      const path = `${clientId}/${uploadId}/${String(i + 1).padStart(2, '0')}-${safeName(file.name)}`;
      const { error } = await supabase.storage.from('adhoc-uploads')
        .upload(path, file, { upsert: true, contentType: MIME[extOf(file.name)] });
      if (error) { setBusy(null); setErr(`${file.name}: ${error.message}`); return; }
      uploaded.push({ path, name: file.name, use_image: isImage(file.name) && use_image });
    }
    setBusy('Starting the draft…');
    const { data, error } = await supabase.rpc('portal_create_adhoc', { p_client_id: clientId, p_input: {
      title: title.trim(), post_type: type, text, notes: notes.trim(), length_band: band, files: uploaded } });
    setBusy(null);
    if (error) { setErr(error.message); return; }
    navigate(clientPath(`/admin/clients/${clientId}/adhoc/${data}`, clientId));
  }

  return (
    <form className="client-form" onSubmit={generate}>
      <h1>New ad hoc blog: {name}</h1>
      <p className="lede">Written in {name}'s voice from the material you give it. Agency only: the client never sees it. Approving builds a ready-to-post package to download.</p>
      <section className="form-section">
        <Field label="Working title or topic"><input value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} required /></Field>
        <div className="field approval-fields" role="radiogroup" aria-label="Post type">
          <span className="field-label">Post type</span>
          {ADHOC_TYPES.map(([k, label]) => (
            <label key={k} className="check approval-option">
              <input type="radio" name="adhoc-type" checked={type === k} onChange={() => setType(k)} />
              <span><span className="approval-option-label">{label}</span></span>
            </label>
          ))}
        </div>
        <div className="field approval-fields" role="radiogroup" aria-label="Length">
          <span className="field-label">Length</span>
          <label className="check approval-option"><input type="radio" name="adhoc-band" checked={band === 'utility'} onChange={() => setBand('utility')} />
            <span><span className="approval-option-label">Utility</span><span className="field-hint">Practical, about 1,200 to 1,500 words.</span></span></label>
          <label className="check approval-option"><input type="radio" name="adhoc-band" checked={band === 'flagship'} onChange={() => setBand('flagship')} />
            <span><span className="approval-option-label">Flagship</span><span className="field-hint">Deep, about 1,800 to 2,000 words.</span></span></label>
        </div>
        <Field label="Paste text (optional)" hint="Interview notes, an email from the client, a project write-up. Any length.">
          <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
        <div className="field">
          <span className="field-label">Files (optional)</span>
          <span className="field-hint">pdf, docx, txt, md or photos. Up to {MAX_FILES} files, 20 MB each. Stored privately; only Digital Lotus can open them.</span>
          <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.md,image/*" onChange={(e) => addFiles(e.target.files)} />
          {files.length > 0 && (
            <ul className="adhoc-files">
              {files.map((f, i) => (
                <li key={i}>
                  {f.file.name} <span className="hint" style={{ margin: 0 }}>({Math.ceil(f.file.size / 1024)} KB)</span>
                  {isImage(f.file.name) && (
                    <label className="check" style={{ display: 'inline-flex', marginLeft: 12 }}>
                      <input type="checkbox" checked={f.use_image}
                             onChange={(e) => setFiles(files.map((x, j) => (j === i ? { ...x, use_image: e.target.checked } : x)))} />
                      <span>Use this photo in the post</span>
                    </label>
                  )}
                  {' '}<button type="button" className="link-btn" onClick={() => setFiles(files.filter((_, j) => j !== i))}>Remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Field label="Context notes (optional)" hint="Angle, who it's for, anything to include or leave out.">
          <textarea rows={3} value={notes} maxLength={10000} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </section>
      {err && <div className="banner banner-err">{err}</div>}
      {busy && <Working text={busy} />}
      <div className="review-actions">
        <button type="submit" className="btn btn-primary" disabled={!!busy}>Generate</button>
      </div>
    </form>
  );
}

function AdhocJob({ clientId, jobId }) {
  // The client this page is ABOUT comes from the URL, not from whoever is
  // active in the switcher, so the posting instructions are looked up by that
  // id. Using activeClient here would show Squarespace steps for a WordPress
  // client any time the two disagreed.
  const { clients } = useAuth();
  const platform = (clients || []).find((c) => c.client_id === clientId)?.platform;
  const [job, setJob] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [err, setErr] = useState(null);
  const [banner, setBanner] = useState(null);
  const [editing, setEditing] = useState(false);
  // Session 32: the article and the summary are edited through the shared
  // review component now, the same way a client edits theirs. Only the title
  // stays here, because the client flow has never offered the title and the
  // agency still needs it.
  const [draft, setDraft] = useState({ title: '' });
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('v_portal_adhoc').select('*').eq('job_id', jobId).maybeSingle();
    if (error) { setErr(error.message); return; }
    setJob(data || false);
    // Session 32: the styled preview, exactly as the client review page loads
    // it. A draft generated before this session has no preview package and
    // falls back to rendering the markdown, which is what it always did.
    if (data?.preview_package_path) {
      const { data: sig, error: sErr } = await supabase.storage
        .from('post-packages').createSignedUrl(data.preview_package_path, 900);
      if (!sErr) {
        try {
          const resp = await fetch(sig.signedUrl);
          setPreviewHtml(resp.ok ? await resp.text() : null);
        } catch (e) { console.warn('preview fetch failed:', e); setPreviewHtml(null); }
      }
    } else {
      setPreviewHtml(null);
    }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const inFlight = job && (IN_FLIGHT.includes(job.status) || (job.status === 'done' && !job.package_ready));
  useEffect(() => {
    if (!inFlight) return undefined;
    let cancelled = false;
    (async () => {
      const start = job.status;
      await pollUntil(async () => {
        const { data } = await supabase.from('v_portal_adhoc').select('status, package_ready').eq('job_id', jobId).maybeSingle();
        return data && (data.status !== start || (data.status === 'done' && data.package_ready));
      }, { every: 10000, timeout: 30 * 60000 });
      if (!cancelled) load();
    })();
    return () => { cancelled = true; };
  }, [inFlight, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Session 32: the same uploader the client review page uses. The ad hoc
  // slug is the post's own slug, so uploads land beside a post draft's in
  // post-images/<client>/<slug>/<slot>/.
  const { uploading, skipping, onUploadSlot, onToggleSkip } = useSlotUploads({
    jobId,
    clientId,
    slug: job?.slug || jobId,
    reload: load,
    onBanner: setBanner,
  });

  const fills = useMemo(() => {
    const by = {};
    for (const f of (job?.image_slot_fills || [])) if (f && f.slot_id) by[f.slot_id] = f;
    return by;
  }, [job]);

  if (err) return <div className="empty"><h2>Couldn't load the ad hoc blog</h2><p>{err}</p></div>;
  if (job === null) return <p className="lede">Loading…</p>;
  if (job === false) return <div className="empty"><h2>Not found</h2></div>;

  const review = job.status === 'agency_review';
  const warnings = job.self_check?.warnings || [];

  async function call(fn, args, ok) {
    setBusy(true); setBanner(null);
    const { error } = await supabase.rpc(fn, args);
    setBusy(false);
    if (error) { setBanner({ tone: 'err', text: error.message }); return false; }
    setBanner({ tone: 'ok', text: ok });
    await load();
    return true;
  }
  async function saveEdit() {
    const fields = {};
    if ((draft.title ?? '') !== (job.title ?? '')) fields.title = draft.title;
    if (!Object.keys(fields).length) { setEditing(false); return; }
    if (await call('portal_edit_adhoc', { p_job_id: jobId, p_fields: fields }, 'Saved.')) setEditing(false);
  }
  async function download() {
    const { data, error } = await supabase.storage.from('post-packages').createSignedUrl(job.package_path, 300);
    if (error) { setBanner({ tone: 'err', text: error.message }); return; }
    window.location.href = data.signedUrl;
  }

  return (
    <>
      <h1>{job.title} <span className="mode-tag">Ad hoc</span></h1>
      <p className="lede">{TYPE_LABEL[job.adhoc_type] || job.adhoc_type} · {job.length_band} · {STATUS_TEXT[job.status] || job.status}
        {job.revision > 0 && ` · revision ${job.revision}`}</p>
      {inFlight && <Working text={STATUS_TEXT[job.status] || 'Working…'} />}
      {job.error && <div className="banner banner-err">{job.error}</div>}
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}

      {job.status === 'done' && job.package_ready && (
        <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="btn btn-primary" onClick={download}>Download package</button>
          <PostingInstructions platform={platform} />
          <span className="hint" style={{ margin: 0 }}>Same ready-to-post format as handoff posts. Not scheduled or published.</span>
        </div>
      )}

      {warnings.length > 0 && review && (
        <section className="review-section">
          <h2>Self-check</h2>
          <ul>{warnings.map((w, i) => <li key={i}>{typeof w === 'string' ? w : JSON.stringify(w)}</li>)}</ul>
        </section>
      )}

      {/* Session 32: from here down this page is the client review page.
          Leena reviews Landry's ad hoc drafts to walk the client's flow, so
          the same components render it rather than a copy of them. */}
      {review && <ReviewCallout canEdit />}

      {job.body_md && (
        <PostAndEditor
          key={job.job_id}
          draft={job}
          canEdit={review}
          previewHtml={previewHtml}
          slug={job.slug || jobId}
          onSaved={load}
          previewFresh={!job.body_md_edited
            || (!!job.preview_uploaded_at && !!job.body_edited_at && new Date(job.preview_uploaded_at) >= new Date(job.body_edited_at))}
        />
      )}

      {job.body_md && (
        <ImageSlotsSection
          draft={job}
          canEdit={review}
          fills={fills}
          uploading={uploading}
          skipping={skipping}
          onUploadSlot={onUploadSlot}
          onToggleSkip={onToggleSkip}
          onChanged={load}
          needsHero={false}
        />
      )}

      {review && editing && (
        <section className="review-section">
          <Field label="Title (agency only — a client never edits this)">
            <input value={draft.title} maxLength={300}
                   onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          </Field>
          <div className="review-actions">
            <button type="button" className="link-btn" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={saveEdit}>Save title</button>
          </div>
        </section>
      )}

      {review && !editing && (
        <section className="review-section">
          <div className="review-actions" style={{ justifyContent: 'flex-start', marginTop: 0 }}>
            <button type="button" className="link-btn" disabled={busy}
                    onClick={() => { setDraft({ title: job.title || '' }); setEditing(true); }}>
              Agency: edit the title
            </button>
          </div>
          <label className="field" style={{ marginTop: 16 }}>
            <span className="field-label">Request changes</span>
            <textarea className="review-textarea" rows={3} value={notes} maxLength={10000} onChange={(e) => setNotes(e.target.value)}
                      placeholder="What should the next version change?" />
          </label>
          <div className="review-actions">
            <button type="button" className="btn" disabled={busy || !notes.trim()}
                    onClick={async () => { if (await call('portal_revise_adhoc', { p_job_id: jobId, p_notes: notes.trim() }, 'Sent. The revision comes back here.')) setNotes(''); }}>
              Request changes
            </button>
            <button type="button" className="btn btn-primary" disabled={busy}
                    onClick={() => call('portal_approve_adhoc', { p_job_id: jobId }, 'Approved. Building the package.')}>
              Approve
            </button>
          </div>
        </section>
      )}

      <section className="review-section">
        <h2>Source material</h2>
        <ul>
          {job.source_text_chars > 0 && <li>Pasted text ({job.source_text_chars.toLocaleString()} characters)</li>}
          {(job.files || []).map((f) => <li key={f.path}>{f.name}{f.use_image ? ' (used in the post)' : ''}</li>)}
          {job.notes && <li>Notes: {job.notes}</li>}
        </ul>
        {(job.revise_notes || []).length > 0 && (
          <>
            <h2>Change requests</h2>
            <ul>{job.revise_notes.map((r, i) => <li key={i}>{fmtDateTime(r.at)}: {r.notes}</li>)}</ul>
          </>
        )}
      </section>
      <p className="hint"><Link to={clientPath(`/admin/clients/${clientId}/adhoc`, clientId)}>All ad hoc blogs</Link></p>
    </>
  );
}
